import { identify, reloadFeatureFlags, syncTerreiroGroup } from "./posthog";
import type { ProductProfileType } from "./product";

export type SessionIdentityProperties = {
  email?: string | null;
  name?: string | null;
  roleSlugs?: string[];
  permissionDomains?: string[];
  activeProfileType?: ProductProfileType;
  terreiroId?: string | null;
};

export type IdentifyMutation = {
  set: Record<string, unknown>;
  unset: string[];
};

const derivePermissionDomains = (permissionCodes: string[]): string[] => {
  const domains = new Set<string>();
  if (
    permissionCodes.some(
      (code) => code.startsWith("activity.create") || code === "admin.full_access",
    )
  ) {
    domains.add("activities");
  }
  if (
    permissionCodes.some(
      (code) => code.startsWith("event.create") || code === "admin.full_access",
    )
  ) {
    domains.add("events");
  }
  if (
    permissionCodes.some(
      (code) =>
        code.startsWith("financial.create_") ||
        code.startsWith("financial.update_") ||
        code.startsWith("financial.confirm_transactions"),
    )
  ) {
    domains.add("financial");
  }
  if (permissionCodes.some((code) => code.includes("participation"))) {
    domains.add("participation");
  }
  return Array.from(domains);
};

export const toIdentifyMutation = (
  properties: SessionIdentityProperties = {},
): IdentifyMutation => {
  const activeProfileType =
    properties.activeProfileType ??
    (properties.terreiroId ? "terreiro" : "personal");
  const set: Record<string, unknown> = {
    active_profile_type: activeProfileType,
  };
  const unset: string[] = [];

  if (properties.email) {
    set.email = properties.email;
  }
  if (properties.name) {
    set.name = properties.name;
  }
  if (properties.terreiroId) {
    set.terreiro_id = properties.terreiroId;
  } else if (properties.terreiroId === null || activeProfileType === "personal") {
    unset.push("terreiro_id");
  }
  if (properties.roleSlugs && properties.roleSlugs.length > 0) {
    set.role_slugs = properties.roleSlugs;
  } else if (properties.roleSlugs) {
    unset.push("role_slugs");
  }
  if (properties.permissionDomains && properties.permissionDomains.length > 0) {
    set.permission_domains = properties.permissionDomains;
  } else if (properties.permissionDomains) {
    unset.push("permission_domains");
  }

  return { set, unset };
};

export const identifyAuthenticatedUser = (
  userId: string,
  properties: SessionIdentityProperties = {},
): void => {
  const { set, unset } = toIdentifyMutation(properties);
  identify(userId, set, unset);
  syncTerreiroGroup(properties.terreiroId);
  reloadFeatureFlags();
};

export { derivePermissionDomains };
